import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { User } from '../../../models/auth';
import { AuthService } from '../../../services/auth.service';
import { EncryptionService } from '../../../services/encryption.service';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';

/**
 * Fixed ON / OFF button (shown above the AI icon) to switch the driver active status.
 * Only for users who can toggle "Active" (drivers and self service providers).
 */
@Component({
  selector: 'logibids-driver-active-toggle',
  imports: [NgOptimizedImage, TranslatePipe],
  templateUrl: './driver-active-toggle.component.html',
  styleUrl: './driver-active-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverActiveToggleComponent {
  private readonly authService = inject(AuthService);
  private readonly encryptionService = inject(EncryptionService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly user = computed(() => this.authService.profileDetails());

  readonly canToggle = computed(() => {
    const user = this.user();
    return (
      user?.type === 'DRIVER' ||
      (user?.type === 'SERVICE_PROVIDER' &&
        user.service_provider?.self === true)
    );
  });

  readonly active = computed(() => this.user()?.active === true);
  readonly saving = signal(false);

  toggle(): void {
    if (this.saving()) {
      return;
    }
    this.saving.set(true);
    const payload = { active: !this.active() } as User;
    this.authService
      .updateProfile(payload)
      .pipe(
        switchMap(() => this.authService.userProfile()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: user => {
          this.encryptionService.encryptLocalStorage(user, 'profile');
          this.saving.set(false);
        },
        error: () => this.saving.set(false),
      });
  }
}
